import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Cookie, X, Settings } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { initAnalytics } from '../lib/analytics';

interface CookieConsent {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
}

export default function CookieBanner() {
  const { user } = useAuth();
  const [isVisible, setIsVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [consent, setConsent] = useState<CookieConsent>({
    necessary: true,
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    const savedConsent = localStorage.getItem('cookie_consent');
    if (!savedConsent) {
      setIsVisible(true);
    } else {
      const parsed = JSON.parse(savedConsent);
      setConsent(parsed);
      applyConsent(parsed);
    }
  }, []);

  const generateSessionId = () => {
    try {
      if (typeof crypto !== 'undefined') {
        if (typeof crypto.randomUUID === 'function') {
          return crypto.randomUUID();
        }
        if (typeof crypto.getRandomValues === 'function') {
          const buf = new Uint32Array(4);
          crypto.getRandomValues(buf);
          return Array.from(buf, (n) => n.toString(16).padStart(8, '0')).join('');
        }
      }
    } catch (e) {
      console.warn('Falling back to Math.random session id', e);
    }
    return `sess_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
  };

  const applyConsent = (consentData: CookieConsent) => {
    if (consentData.analytics) {
      console.log('Analytics cookies enabled');
    } else {
      console.log('Analytics cookies disabled');
    }

    if (consentData.marketing) {
      console.log('Marketing cookies enabled');
    } else {
      console.log('Marketing cookies disabled');
    }
  };

  const saveConsent = async (consentData: CookieConsent) => {
    localStorage.setItem('cookie_consent', JSON.stringify(consentData));
    localStorage.setItem('cookie_consent_date', new Date().toISOString());

    const sessionId = localStorage.getItem('cookie_consent_session') || generateSessionId();
    localStorage.setItem('cookie_consent_session', sessionId);

    try {
      await supabase.from('cookie_consent').insert({
        user_id: user?.id ?? null,
        session_id: sessionId,
        necessary_cookies: consentData.necessary,
        analytics_cookies: consentData.analytics,
        marketing_cookies: consentData.marketing,
      });
    } catch (error) {
      // Si RLS o configuración falla, al menos seguimos aplicando en localStorage
      console.error('Error saving cookie consent to database:', error);
    }

    applyConsent(consentData);
    initAnalytics(consentData);
    setIsVisible(false);
  };

  const acceptAll = () => {
    const allConsent = { necessary: true, analytics: true, marketing: true };
    setConsent(allConsent);
    saveConsent(allConsent);
  };

  const rejectOptional = () => {
    const minimalConsent = { necessary: true, analytics: false, marketing: false };
    setConsent(minimalConsent);
    saveConsent(minimalConsent);
  };

  const saveCustom = () => {
    saveConsent(consent);
  };

  if (!isVisible) return null;

  return (
    <div
      style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        backgroundColor: 'white',
        boxShadow: '0 -4px 24px rgba(0, 0, 0, 0.15)',
        zIndex: 9999,
        padding: '24px',
        borderTop: '1px solid var(--border)',
      }}
    >
      <div
        style={{
          maxWidth: '1200px',
          margin: '0 auto',
        }}
      >
        {!showSettings ? (
          <>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
              <div
                style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '12px',
                  backgroundColor: '#f9fafb',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <Cookie size={24} color="var(--brand)" />
              </div>

              <div style={{ flex: 1 }}>
                <h3
                  style={{
                    margin: '0 0 8px',
                    fontSize: '18px',
                    fontWeight: 700,
                    color: 'var(--text)',
                  }}
                >
                  Usamos cookies para mejorar tu experiencia
                </h3>
                <p
                  style={{
                    margin: '0 0 16px',
                    fontSize: '14px',
                    lineHeight: '1.6',
                    color: 'var(--muted)',
                  }}
                >
                  Utilizamos cookies necesarias para el funcionamiento del sitio y cookies
                  opcionales de analítica para mejorar nuestro servicio. Puedes aceptar todas,
                  rechazar las opcionales o configurarlas según tus preferencias.{' '}
                  <Link
                    to="/cookies"
                    style={{ color: 'var(--brand)', textDecoration: 'underline' }}
                  >
                    Más información
                  </Link>
                </p>

                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                  <button
                    onClick={acceptAll}
                    style={{
                      padding: '12px 24px',
                      backgroundColor: 'var(--brand)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'var(--brand-ink)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'var(--brand)';
                    }}
                  >
                    Aceptar todo
                  </button>

                  <button
                    onClick={rejectOptional}
                    style={{
                      padding: '12px 24px',
                      backgroundColor: 'white',
                      color: 'var(--text)',
                      border: '2px solid var(--border)',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#f9fafb';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'white';
                    }}
                  >
                    Solo necesarias
                  </button>

                  <button
                    onClick={() => setShowSettings(true)}
                    style={{
                      padding: '12px 24px',
                      backgroundColor: 'white',
                      color: 'var(--text)',
                      border: '2px solid var(--border)',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      transition: 'all 0.2s',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#f9fafb';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'white';
                    }}
                  >
                    <Settings size={16} />
                    Configurar
                  </button>
                </div>
              </div>
            </div>
          </>
        ) : (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '24px' }}>
              <h3
                style={{
                  margin: 0,
                  fontSize: '20px',
                  fontWeight: 700,
                  color: 'var(--text)',
                }}
              >
                Configurar cookies
              </h3>
              <button
                onClick={() => setShowSettings(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px',
                }}
              >
                <X size={24} color="var(--muted)" />
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div
                style={{
                  padding: '16px',
                  backgroundColor: '#f9fafb',
                  borderRadius: '8px',
                  border: '1px solid var(--border)',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <h4 style={{ margin: 0, fontSize: '16px', fontWeight: 600 }}>
                    Cookies necesarias
                  </h4>
                  <span
                    style={{
                      fontSize: '12px',
                      fontWeight: 600,
                      color: 'var(--muted)',
                      padding: '4px 8px',
                      backgroundColor: 'white',
                      borderRadius: '4px',
                    }}
                  >
                    Siempre activas
                  </span>
                </div>
                <p style={{ margin: 0, fontSize: '13px', color: 'var(--muted)', lineHeight: '1.5' }}>
                  Esenciales para el funcionamiento del sitio web. Incluyen autenticación, sesión y
                  preferencias.
                </p>
              </div>

              <div
                style={{
                  padding: '16px',
                  backgroundColor: consent.analytics ? '#f0fdf4' : '#f9fafb',
                  borderRadius: '8px',
                  border: `1px solid ${consent.analytics ? '#86efac' : 'var(--border)'}`,
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <h4 style={{ margin: 0, fontSize: '16px', fontWeight: 600 }}>
                    Cookies de analítica
                  </h4>
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={consent.analytics}
                      onChange={(e) => setConsent({ ...consent, analytics: e.target.checked })}
                      style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                    />
                  </label>
                </div>
                <p style={{ margin: 0, fontSize: '13px', color: 'var(--muted)', lineHeight: '1.5' }}>
                  Nos ayudan a entender cómo utilizas la aplicación para mejorarla (PostHog).
                </p>
              </div>

              <div
                style={{
                  padding: '16px',
                  backgroundColor: consent.marketing ? '#f0fdf4' : '#f9fafb',
                  borderRadius: '8px',
                  border: `1px solid ${consent.marketing ? '#86efac' : 'var(--border)'}`,
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <h4 style={{ margin: 0, fontSize: '16px', fontWeight: 600 }}>
                    Cookies de marketing
                  </h4>
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={consent.marketing}
                      onChange={(e) => setConsent({ ...consent, marketing: e.target.checked })}
                      style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                    />
                  </label>
                </div>
                <p style={{ margin: 0, fontSize: '13px', color: 'var(--muted)', lineHeight: '1.5' }}>
                  Actualmente no utilizamos cookies de marketing. Si lo hacemos en el futuro, te
                  pediremos consentimiento.
                </p>
              </div>
            </div>

            <div style={{ marginTop: '24px', display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowSettings(false)}
                style={{
                  padding: '12px 24px',
                  backgroundColor: 'white',
                  color: 'var(--text)',
                  border: '2px solid var(--border)',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#f9fafb';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'white';
                }}
              >
                Cancelar
              </button>
              <button
                onClick={saveCustom}
                style={{
                  padding: '12px 24px',
                  backgroundColor: 'var(--brand)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--brand-ink)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--brand)';
                }}
              >
                Guardar preferencias
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
